import _ from 'underscore';
import { getDateRangeParams, createFolderFilterQuery } from '@bingads-webui-campaign-react/asset-library-model';

export const createFolderExpandQuery = ({
  includeAssets,
  includeSubfolders,
  includeParentHierarchy,
}) => {
  const expandItems = [];

  if (includeAssets) {
    expandItems.push('Assets');
  }
  if (includeSubfolders) {
    const expandAssetsInSubfolders = includeAssets ? `$expand=Assets${includeParentHierarchy ? ',ParentHierarchy' : ''};` : '';
    expandItems.push(`SubFolders(${expandAssetsInSubfolders})`);
  }
  if (includeParentHierarchy) {
    expandItems.push('ParentHierarchy');
  }

  const expandQuery = expandItems.length > 0 ? `$expand=${expandItems.join(',')}` : '';

  return expandQuery;
};

const createDateRangeQuery = ({
  startDate, endDate, comparisonStartDate, comparisonEndDate,
}) => {
  if (startDate && endDate) {
    const startDateParam = `startDate=${startDate}`;
    const endDateParam = `endDate=${endDate}`;
    const comparisonStartDateParam = `comparisonStartDate=${comparisonStartDate || null}`;
    const comparisonEndDateParam = `comparisonEndDate=${comparisonEndDate || null}`;
    return [startDateParam, endDateParam, comparisonStartDateParam, comparisonEndDateParam];
  }
  return [];
};

export const createFolderQuery = ({
  filterArgs,
  expandArgs,
  sortBy,
  sortDirection,
  assetsToLoadForSubfolders,
  isGlobalSearch = false,
  isAssetPickerScenario = false,
  folderId, // used for TimeSeries API, not for GetFolder API
}) => {
  let sortQuery = sortBy ? `$orderby=${sortBy}` : '';
  if (sortQuery && sortDirection) {
    sortQuery += ` ${sortDirection}`;
  }

  const filterQuery = createFolderFilterQuery({ ...filterArgs, folderId });

  const selectQuery = '$select=Id,ParentFolderId,Name,ItemsCount';

  const expandQuery = createFolderExpandQuery(expandArgs);

  let assetSubfolderQuery = '';
  if (assetsToLoadForSubfolders) {
    assetSubfolderQuery = `maxsubfolderassetscount=${assetsToLoadForSubfolders}`;
  }

  let globalSearchQuery = '';
  if (isGlobalSearch) {
    globalSearchQuery = 'isglobalsearch=true';
  }

  let assetPickerQuery = '';
  if (isAssetPickerScenario) {
    assetPickerQuery = 'isassetpickerscenario=true';
  }

  const params = _.compact([sortQuery, filterQuery, selectQuery, expandQuery, assetSubfolderQuery, globalSearchQuery, assetPickerQuery]);

  return params.join('&');
};

export const getFolderODataService = ({
  odata, legacyOdata, oDataConfig, aid, cid, i18n, dateFormat,
}) => {
  const getRequestUrl = ({ folderId, funcPath, query } = {}) => {
    let resourceRootUrl = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Folders`;

    if (folderId) {
      resourceRootUrl += `(${folderId})`;
    }

    if (funcPath) {
      resourceRootUrl += `/${funcPath}`;
    }

    return query ? `${resourceRootUrl}?${query}` : resourceRootUrl;
  };

  const getFolderData = ({
    skip,
    count,
    folderId,
    expandArgs,
    filterArgs,
    sortBy,
    sortDirection,
    assetsToLoadForSubfolders,
    isGlobalSearch = false,
    isAssetPickerScenario = false,
  }) => {
    const folderQuery = createFolderQuery({
      filterArgs,
      expandArgs,
      sortBy,
      sortDirection,
      assetsToLoadForSubfolders,
      isGlobalSearch,
      isAssetPickerScenario,
    });

    const url = getRequestUrl({
      folderId,
      funcPath: folderId ? '' : 'Default.GetRootFolder',
      query: folderQuery,
    });

    return odata.get(url, _.extend({
      data: {
        $top: count,
        $skip: skip,
        count: true,
      },
    }, oDataConfig));
  };

  const getFolderDataWithBIData = ({
    perfMetricDateRange,
    skip,
    count,
    folderId,
    expandArgs,
    filterArgs,
    sortBy,
    sortDirection,
    assetsToLoadForSubfolders,
    isGlobalSearch = false,
    isAssetPickerScenario = false,
  }) => {
    const folderQuery = createFolderQuery({
      filterArgs,
      expandArgs,
      sortBy,
      sortDirection,
      assetsToLoadForSubfolders,
      isGlobalSearch,
      isAssetPickerScenario,
    });

    const url = getRequestUrl({
      folderId,
      funcPath: folderId ? 'Default.GetFolderWithBIData' : 'Default.GetRootFolderWithBIData',
      query: folderQuery,
    });

    const {
      startDate, endDate, comparisonStartDate, comparisonEndDate,
    } = getDateRangeParams(i18n, perfMetricDateRange, dateFormat, perfMetricDateRange && perfMetricDateRange.Rangelcid);
    // compose request body
    // const { Granularity, Metrics, MovingAverage } = perfTimeSeriesParams || {};
    const requestBody = {
      gridDataParameters: {
        DateRange: { StartDate: startDate || null, EndDate: endDate || null },
        ComparisonDateRange: comparisonStartDate ? { StartDate: comparisonStartDate, EndDate: comparisonEndDate } : undefined,
        CurrentCustomerId: cid,
      },
      // performanceTimeSeriesParameters: {
      //   Granularity, // 'Hour',
      //   Metrics, // "Impressions,Clicks",
      //   MovingAverage, // true
      // },
    };

    return odata.post(url, _.extend({
      data: requestBody,
      urlParameters: {
        $top: count,
        $skip: skip,
        count: true,
      },
    }, oDataConfig));
  };

  const getPerfTimeSeriesData = ({
    perfMetricDateRange,
    perfTimeSeriesParams,
    folderId,
    filterArgs,
    isGlobalSearch = false,
  }) => {
    const {
      granularity, metric: metrics, isMovingAverageOn: movingAverage,
    } = perfTimeSeriesParams || {};
    const funcParams = [
      ...createDateRangeQuery(getDateRangeParams(i18n, perfMetricDateRange, dateFormat, perfMetricDateRange && perfMetricDateRange.Rangelcid)),
      `granularity=Enum.Granularity'${granularity}'`,
      `metrics='${metrics}'`,
      `movingAverage=${movingAverage || false}`,
    ].join(',');

    // no filter id means global search
    let filterQuery = createFolderFilterQuery(isGlobalSearch ? filterArgs : { ...filterArgs, folderId });
    if (filterQuery) {
      filterQuery = `?${filterQuery}`;
    }
    const url = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Images/Default.PerformanceTimeSeries(${funcParams})${filterQuery}`;

    return odata.get(url, _.extend({
      data: {
        $top: 20, // top is required in the API call
        count: true,
      },
    }, oDataConfig));
  };

  const createFolder = ({ folderName, parentFolderId, folderType }) => {
    const url = getRequestUrl();
    const requestBody = {
      Name: folderName,
      ParentFolderId: parentFolderId,
      FolderType: folderType,
    };

    return odata.post(url, _.extend({ data: requestBody }, oDataConfig))
      .then(res => res.value);
  };

  const renameFolder = ({ folderId, newFolderName }) => {
    const url = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Folders(${folderId})`;
    const requestBody = {
      Name: newFolderName,
      Id: folderId,
    };
    return odata.patch(url, _.extend({ data: requestBody }, oDataConfig))
      .then(res => res && res.value);
  };

  const deleteFolder = ({ folderId }) => {
    const url = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Folders(${folderId})`;
    return odata.delete(url, _.extend({}, oDataConfig))
      .then(res => res && res.value);
  };

  const batchMove = ({
    destFolderId, assets, folders,
  }) => {
    const url = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Folders/Default.PutResources`;
    const requestBody = {
      DestFolderId: destFolderId || null,
      Assets: assets,
      FolderIds: folders,
    };
    const dataJson = JSON.stringifyWithBigInt(requestBody);
    return odata.post(url, _.extend({ data: dataJson }, oDataConfig));
  };

  const addAssets = ({
    folderId,
    asset,
    inProgress = null,
    supportUpscale,
  }) => {
    const url = getRequestUrl({ funcPath: 'Default.AddAsset' });
    const requestBody = {
      FolderId: folderId,
      Asset: asset,
      SupportUpscale: supportUpscale,
    };

    return odata.post(url, _.extend({ data: requestBody, progress: inProgress }, oDataConfig))
      .then(res => res.value);
  };

  const deleteAsset = (asset) => {
    const url = getRequestUrl({ funcPath: 'Default.DeleteAsset' });
    const dataJson = JSON.stringifyWithBigInt(asset);
    return odata.post(url, _.extend({ data: dataJson }, oDataConfig))
      .then(res => res.value);
  };

  const bulkDeleteAssets = (assets) => {
    const url = `${oDataConfig.oDataPath}/Customers(${cid})/Accounts(${aid})/Default.BulkUpsert`;
    const csvHeader = 'Type,Name,Id,Parent Id,Status';
    const csvForamt = 'Format Version,6,,,';
    const csvRows = _.map(assets, ({ assetId, folderAssetType }) => `${folderAssetType},,${assetId},${aid},Deleted`);
    return odata.post(url, _.extend({
      data: {
        CsvRows: [
          csvHeader, csvForamt, ...csvRows,
        ],
      },
    }, oDataConfig)).then((res) => {
      if (_.isEmpty(res)) {
        return [];
      }
      // skip header and format rows, and only keep error rows
      const errors = _.chain(res.slice(2))
        .filter(([type]) => type.indexOf('Error') >= 0)
        // eslint-disable-next-line no-unused-vars
        .map(([type, colStatus, Id, colAID, colName, ErrorCode, ErrorNumber]) => ({ Id, Code: ErrorCode, ErrorNumber }))
        .value();
      return errors;
    });
  };

  const updateAsset = (asset) => {
    const url = getRequestUrl({ funcPath: 'Default.UpdateAsset' });
    const dataJson = JSON.stringifyWithBigInt(asset);
    return odata.post(url, _.extend({ data: dataJson }, oDataConfig))
      .then(res => res.value);
  };

  // fileExtension: e.g. .mp4
  const getBlobUploadUrl = ({ fileExtension }) => {
    const url = getRequestUrl({ funcPath: `Default.GetVideoUploadUrl(fileExtension='${fileExtension}')` });

    return odata.get(url, oDataConfig);
  };

  const getVideo = ({ id }) => {
    const baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})`;
    return odata.get(`${baseURL}/Videos/${id}`, oDataConfig);
  };

  const getVideosByIdsAsync = (videoTasks) => {
    const reqs = _.map(videoTasks, (id) => {
      const baseURL = `/Customers(${cid})/Accounts(${aid})`;
      return {
        type: 'GET',
        url: `${baseURL}/Videos/${id}`,
      };
    });
    // use batch function of legacyOdata instead of odata to avoid some hard code
    return legacyOdata.batch({
      reqs,
      needContentType: true,
    }).then(response => response.map(item => item.data));
  };

  return {
    getFolderData,
    getFolderDataWithBIData,
    getPerfTimeSeriesData,
    createFolder,
    renameFolder,
    deleteFolder,
    addAssets,
    deleteAsset,
    bulkDeleteAssets,
    updateAsset,
    getBlobUploadUrl,
    batchMove,
    getVideosByIdsAsync,
    getVideo,
  };
};
